import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { colores, fuentes } from '../../styles/variablesCss';
import { useUser } from '../../context/UserContext';
import CheckoutSteps from './CheckoutSteps';
import ResumenCompra from './ResumenCompra';
import FormasDePago from './FormasDePago';
import Envio from './Envio';
import Retiro from './Retiro';
import DatosFacturacion from './DatosFacturacion';
import HorarioRetiro from './HorarioRetiro';
import ResumenPedido from './ResumenPedido';
import { BACKEND_URL, MONTO_MINIMO_ENVIO_GRATIS } from '../../Config/Const';
import Storage from '../../utils/Storage';
import { useCheckoutContext } from '../../context/CheckoutContext';
import { useCart } from '../../context/CartContext';
  

const GestionFinalizar = ({ route }) => {
  const navigation = useNavigation();
  const [cupon, setCupon] = useState('');
  const [descuentoAplicado, setDescuentoAplicado] = useState(0);
  const [descuentoAPI, setDescuentoAPI] = useState(0); // Descuento que viene de la API
  const [cargando, setCargando] = useState(false);
  const [showFormaPago, setShowFormaPago] = useState(false); // Estado para mostrar/ocultar el formulario de pago
  const [effectiveTotal, setEffectiveTotal] = useState(0); // Total efectivo a pagar (incluye cupón/flete)
  // Estado para controlar qué secciones están expandidas y activas
  const [activeSection, setActiveSection] = useState('formaPago'); // Inicia con método de entrega
  // Estado para controlar qué secciones están habilitadas (pueden ser abiertas)
  const [seccionesHabilitadas, setSeccionesHabilitadas] = useState({
    formaPago: true,
    horarioRetiro: false,
    facturacion: false,
    metodoPago: false
  });
  
  // Estado para controlar responsividad
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 768
  );
  const isMobile = windowWidth < 768;

  // Estado para el modal de flete gratis
  const [modalFleteVisible, setModalFleteVisible] = useState(false);

  // Refs para cada sección desplegable
  const scrollViewRef = useRef(null);
  const formaPagoRef = useRef(null);
  const envioRef = useRef(null);
  const horarioRetiroRef = useRef(null);
  const facturacionRef = useRef(null);
  const metodoPagoRef = useRef(null);
  const resumenPedidoRef = useRef(null);

  // Función para avanzar a la siguiente sección
  const avanzarSeccion = (seccionActual, siguienteSeccion) => { 
    // Habilitar la siguiente sección
    setSeccionesHabilitadas(prev => ({
      ...prev,
      [siguienteSeccion]: true
    }));
    setActiveSection(siguienteSeccion);
    
    // Hacer scroll automático solo en móvil
    if (isMobile && Platform.OS === 'web') {
      setTimeout(() => {
        let targetRef = null;
        
        switch(siguienteSeccion) {
          case 'horarioRetiro':
            targetRef = horarioRetiroRef;
            break;
          case 'facturacion':
            targetRef = facturacionRef;
            break;
          case 'metodoPago':
            targetRef = metodoPagoRef;
            break;
          default:
            break;
        }
        
        if (targetRef && targetRef.current) {
          // Para metodoPago, usar scroll con offset para que no se corte el título
          if (siguienteSeccion === 'metodoPago') {
            const elementPosition = targetRef.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          } else {
            targetRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }
      }, 100); // Pequeño delay para que el contenido se expanda primero
    }
  };
  
  // Función para navegar a un paso específico desde los steps
  const handleStepClick = (stepId) => {
    if (stepId === 'resumen') {
      volverAlResumen();
    } else {
      setShowFormaPago(true);
      setActiveSection(stepId);
      
      // Hacer scroll automático
      if (isMobile && Platform.OS === 'web') {
        setTimeout(() => {
          let targetRef = null;
          
          switch(stepId) {
            case 'formaPago':
              targetRef = formaPagoRef;
              break;
            case 'horarioRetiro':
              targetRef = horarioRetiroRef;
              break;
            case 'facturacion':
              targetRef = facturacionRef;
              break;
            case 'metodoPago':
              targetRef = metodoPagoRef;
              break;
            default:
              break;
          }
          
          if (targetRef && targetRef.current) {
            targetRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    }
  };

  // Función para volver al resumen de compra
  const volverAlResumen = () => {
    setShowFormaPago(false);
    setActiveSection('formaPago');
    // Scroll al top
    if (scrollViewRef && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: true });
    }
  };

  // Función para volver a la sección anterior
  const volverSeccion = (seccionActual, seccionAnterior) => { 
    // Si estamos volviendo desde metodoPago, limpiar los valores de financiación
    if (seccionActual === 'metodoPago') {
      // Verificar si el usuario había aceptado unificar pedido
      if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
        const unificoAceptado = sessionStorage.getItem('unificoAceptado');
        if (unificoAceptado === 'true') {
          // Limpiar la bandera y recargar la página
          sessionStorage.removeItem('unificoAceptado');
          window.location.reload();
          return; // Salir de la función para evitar ejecutar el resto del código
        }
      }
      
      setTarjeta('');
      setCuotas('');
      setCoeficiente('');
      setTotalDeCuota('');
      setTotalFinal('');
      setNroEmision('');
      setNroPlan('');
      setMarcaTarjeta('');
    }
    setActiveSection(seccionAnterior);
    
    // Hacer scroll automático solo en móvil
    if (isMobile && Platform.OS === 'web') {
      setTimeout(() => {
        let targetRef = null;
        
        switch(seccionAnterior) {
          case 'formaPago':
            targetRef = formaPagoRef;
            break;
          case 'horarioRetiro':
            targetRef = horarioRetiroRef;
            break;
          case 'facturacion':
            targetRef = facturacionRef;
            break;
          default:
            break;
        }
        
        if (targetRef && targetRef.current) {
          targetRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100); // Pequeño delay para que el contenido se expanda primero
    }
  };

  const {mail,codUsu } = useUser();
  const { tipoEntrega, setTipoEntrega, codDic, codCompraUnifica, precioFleteDireccion,
    setTarjeta, setCuotas, setCoeficiente, setTotalDeCuota, setTotalFinal, 
    setNroEmision, setNroPlan, setMarcaTarjeta, deseaFactura, setTelefonoDireccionPrincipal,
    fechaRetiroSeleccionada, codigoHorarioSeleccionado } = useCheckoutContext();
  const { totales, productos: productosCarritoContext } = useCart();

  // Verificar si hay productos con días de envío (los días están en masInfo[0].dias)
  const productosConDiasEnvio = productosCarritoContext.filter(p => {
    const dias = p.masInfo && p.masInfo[0] && p.masInfo[0].dias;
    return dias && dias > 0;
  });
  const tieneDiasEnvio = productosConDiasEnvio.length > 0;
  const maxDiasEnvio = tieneDiasEnvio 
    ? Math.max(...productosConDiasEnvio.map(p => p.masInfo[0].dias)) 
    : 0;


  // Effect para detectar si hay un pedido a unificar
  useEffect(() => {
    if (codCompraUnifica > 0) {
      // Si hay un pedido a unificar, ir directo a forma de pago
      setShowFormaPago(true);
      setActiveSection('metodoPago');
    }
  }, [codCompraUnifica]);

  // Effect para limpiar la bandera de unificación cuando el componente se desmonte
  useEffect(() => {
    return () => {
      // Cleanup: limpiar la bandera cuando el usuario sale de la página de checkout
      if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('unificoAceptado');
      }
    };
  }, []);

  // Effect para forzar envío si hay productos con días de envío
  useEffect(() => {
    if (tieneDiasEnvio && tipoEntrega !== 2) {
      setTipoEntrega(2);
    }
  }, [tieneDiasEnvio, tipoEntrega, setTipoEntrega]);

  // Al mostrar el desplegable de Método de entrega, refrescar direcciones para obtener teléfono principal
  useEffect(() => {
    const fallbackFetchDirecciones = async () => {
      try {
        const params = new URLSearchParams();
        if (mail) params.append('mail', mail);
        if (codUsu) params.append('codUsu', codUsu);
        const resp = await fetch(`${BACKEND_URL}/rest/usuarios/direcciones?${params.toString()}`, { method: 'GET', headers: { 'Accept': 'application/json' } });
        if (!resp.ok) return;
        const data = await resp.json();
        const dirs = Array.isArray(data?.dsResultado?.direcciones) ? data.dsResultado.direcciones : [];
        if (dirs.length > 0) {
          const principal = dirs.find(d => d.descripcion === 'PRINCIPAL') || dirs[0];
          const telefono = principal?.tel || principal?.cel || '';
          setTelefonoDireccionPrincipal(telefono || '');
        }
      } catch {}
    };

    if (showFormaPago && activeSection === 'formaPago') {
      if (envioRef && envioRef.current && typeof envioRef.current.cargarDireccionesUsuario === 'function') {
        envioRef.current.cargarDireccionesUsuario();
      } else {
        fallbackFetchDirecciones();
      }
    }
  }, [showFormaPago, activeSection, mail, codUsu, setTelefonoDireccionPrincipal]);

  // Cargar métodos de entrega cuando se entra a la pantalla de método de entrega
  useEffect(() => {
    const cargarMetodosEntrega = async () => {
      if (!showFormaPago || activeSection !== 'formaPago') return;
      
      try {
        setCargandoMetodosEntrega(true);
        const params = new URLSearchParams();
        if (mail) params.append('mail', mail);
        if (codUsu) params.append('codUsu', codUsu);
        
        const response = await fetch(`${BACKEND_URL}/rest/fin/metodoEntrega?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const ultimoMetodo = data?.response?.ultimoMetodoEntrega;
          
          // Seleccionar el método de entrega según la respuesta de la API
          // Solo si no hay productos con días de envío y no hay selección previa
          if (ultimoMetodo && tipoEntrega === null && !tieneDiasEnvio) {
            if (ultimoMetodo === 'ENVIO') {
              setTipoEntrega(2);
            } else if (ultimoMetodo === 'RETIRO') {
              setTipoEntrega(1);
            }
          }
        }
      } catch (error) {
        console.error('Error al cargar métodos de entrega:', error);
      } finally {
        setCargandoMetodosEntrega(false);
      }
    };

    cargarMetodosEntrega();
  }, [showFormaPago, activeSection, mail, codUsu, tipoEntrega, tieneDiasEnvio, setTipoEntrega]);

  // Si el usuario elige Retiro, igualmente obtener teléfono de dirección principal
  useEffect(() => {
    if (showFormaPago && activeSection === 'formaPago' && tipoEntrega === 1) {
      // En Retiro, Envio no está montado; usar fallback
      (async () => {
        try {
          const params = new URLSearchParams();
          if (mail) params.append('mail', mail);
          if (codUsu) params.append('codUsu', codUsu);
          const resp = await fetch(`${BACKEND_URL}/rest/usuarios/direcciones?${params.toString()}`, { method: 'GET', headers: { 'Accept': 'application/json' } });
          if (!resp.ok) return;
          const data = await resp.json();
          const dirs = Array.isArray(data?.dsResultado?.direcciones) ? data.dsResultado.direcciones : [];
          if (dirs.length > 0) {
            const principal = dirs.find(d => d.descripcion === 'PRINCIPAL') || dirs[0];
            const telefono = principal?.tel || principal?.cel || '';
            setTelefonoDireccionPrincipal(telefono || '');
          }
        } catch {}
      })();
    }
  }, [tipoEntrega, showFormaPago, activeSection, mail, codUsu, setTelefonoDireccionPrincipal]);

  // Sistema de inactividad: recargar página después de 15 minutos sin actividad
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Solo en web

    const TIEMPO_INACTIVIDAD = 15 * 60 * 1000; // 15 minutos en milisegundos
    let temporizadorInactividad;

    // Función para resetear el temporizador
    const resetearTemporizador = () => {
      // Limpiar el temporizador anterior
      if (temporizadorInactividad) {
        clearTimeout(temporizadorInactividad);
      }

      // Crear un nuevo temporizador
      temporizadorInactividad = setTimeout(() => {
        // Recargar la página después de 15 minutos de inactividad
        window.location.reload();
      }, TIEMPO_INACTIVIDAD);
    };

    // Eventos que indican actividad del usuario
    const eventosActividad = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Agregar listeners para todos los eventos de actividad
    eventosActividad.forEach(evento => {
      window.addEventListener(evento, resetearTemporizador);
    });

    // Iniciar el temporizador al montar el componente
    resetearTemporizador();

    // Cleanup: remover listeners y limpiar temporizador al desmontar
    return () => {
      if (temporizadorInactividad) {
        clearTimeout(temporizadorInactividad);
      }
      eventosActividad.forEach(evento => {
        window.removeEventListener(evento, resetearTemporizador);
      });
    };
  }, []); // Solo ejecutar al montar/desmontar el componente

  // Estados para la información de la sucursal
  const [sucursalId, setSucursalId] = useState('');
  const [sucursalDireccion, setSucursalDireccion] = useState('');
  const [sucursalCiudad, setSucursalCiudad] = useState('');
  const [sucursalTelefono, setSucursalTelefono] = useState('');

  // Estado para métodos de entrega desde la API
  const [metodosEntrega, setMetodosEntrega] = useState([]);
  const [cargandoMetodosEntrega, setCargandoMetodosEntrega] = useState(false);

  const [productos, setProductos] = useState([]);
  const [cargandoCarrito, setCargandoCarrito] = useState(true);
  const [errorCarrito, setErrorCarrito] = useState(null);


  const cargarProductosCarrito = async () => {
    try {
      setCargandoCarrito(true);
      setErrorCarrito(null);
      const suc = await Storage.getItem('sucursalId');

      // Si no hay usuario autenticado, mostrar estado vacío
      if (!mail && !codUsu) {
        setProductos([]);
        setCargandoCarrito(false);
        return;
      }

      // Construir la URL con query parameters
      const queryParams = new URLSearchParams({
        mail: mail,
        codUsu: codUsu,
        suc: suc
      }).toString();

      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      // Obtener texto primero para depuración
      const responseText = await response.text();

      // Convertir a JSON si es posible
      let data;
      try {
        data = JSON.parse(responseText);

        // Guardar productos y convertir a formato de items
        const productosCarrito = data.dsResultado.productos || [];
        setProductos(productosCarrito);

        // Si la API devuelve información de descuentos, la guardamos
        if (data.dsResultado.total && data.dsResultado.total.length > 0) {
          setDescuentoAPI(data.dsResultado.total[0].descuentos);
        } else {
          setDescuentoAPI(0);
        }

      } catch (e) {
        setErrorCarrito('Error al obtener datos del carrito');
        setProductos([]);
      }
    } catch (err) {
      setErrorCarrito('Error de conexión');
      setProductos([]);
    } finally {
      setCargandoCarrito(false);
    }
  };

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    cargarProductosCarrito();
    cargarDatosSucursal();
    setModalFleteVisible(true);

    // Configurar un listener para cuando la pantalla reciba el foco
    const focusListener = navigation?.addListener?.('focus', () => {
      setModalFleteVisible(true);
    });

    // Limpiar los listeners al desmontar
    return () => focusListener && focusListener();
  }, [navigation]);

  // Función para cargar los datos de la sucursal desde localStorage
  const cargarDatosSucursal = async () => {
    try {
      if (Platform.OS === 'web') {
        // En web, usamos localStorage directamente
        const sucId = localStorage.getItem('sucursalId') || '';
        const direccion = localStorage.getItem('selectedSubBranch') || '';
        const ciudad = localStorage.getItem('ciudadNom') || '';
        const telefono = localStorage.getItem('sucursalDeRetiro') || 'Tel: Consultar'; // Usar sucursalDeRetiro

        setSucursalId(sucId);
        setSucursalDireccion(direccion);
        setSucursalCiudad(ciudad);
        setSucursalTelefono(telefono);
      } else {
        // En React Native, usamos el Storage importado
        const sucId = await Storage.getItem('sucursalId') || '';
        const direccion = await Storage.getItem('selectedSubBranch') || '';
        const ciudad = await Storage.getItem('ciudadNom') || '';
        const telefono = await Storage.getItem('sucursalDeRetiro') || 'Tel: Consultar';

        setSucursalId(sucId);
        setSucursalDireccion(direccion);
        setSucursalCiudad(ciudad);
        setSucursalTelefono(telefono);
      }
    } catch (error) {
      // Silently handle error, showing default values instead
    }
  };

  // Listener para detectar cambios en localStorage y actualizar datos de sucursal
  useEffect(() => {
    // Carga inicial
    cargarDatosSucursal();

    // Listener para detectar cambios en localStorage (solo web)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleStorageChange = (e) => {
        // Detectar cambios en cualquiera de las claves relacionadas con sucursal
        if (e.key === 'ciudadNom' || e.key === 'sucursalId' || e.key === 'selectedSubBranch' || e.key === 'sucursalDeRetiro') {
          cargarDatosSucursal();
        }
      };

      // Listener para cambios personalizados en la misma pestaña
      const handleCustomStorageChange = (e) => {
        if (e.detail?.key === 'ciudadNom' || e.detail?.key === 'sucursalId' || e.detail?.key === 'selectedSubBranch' || e.detail?.key === 'sucursalDeRetiro') {
          cargarDatosSucursal();
        }
      };

      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('localStorageChange', handleCustomStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('localStorageChange', handleCustomStorageChange);
      };
    }
  }, []);

  // Calcular subtotales y total
  const calcularSubtotal = () => {
    return productos.reduce((total, producto) => {
      return total + (producto.precio * (producto.cantCarrito || 1));
    }, 0);
  };

  const subtotal = calcularSubtotal();
  const descuentoTotal = descuentoAplicado + descuentoAPI;
  const percepciones = totales.percepciones || 0;
  
  // Calcular el total de productos (sin flete) para verificar si aplica flete gratis
  // Agregar percepciones solo si el usuario desea factura A o B
  const percepcionesAplicadas = deseaFactura ? percepciones : 0;
  const totalProductos = subtotal - descuentoTotal + percepcionesAplicadas;
  
  // Calcular el flete final
  let fleteAMostrar = 0;
  
  // Si el total de productos es mayor a $100,000, el flete es gratis
  if (totalProductos > MONTO_MINIMO_ENVIO_GRATIS) {
    fleteAMostrar = 0;
  } else {
    // Si no, calcular el flete normalmente
    const fleteCarrito = totales.precioFlete || 0;
    const fleteDireccion = precioFleteDireccion || 0;
    fleteAMostrar = parseInt(tipoEntrega) === 2 
      ? Math.max(fleteCarrito, fleteDireccion) 
      : fleteCarrito;
  }
  
  // Total final incluyendo el flete
  const total = totalProductos + fleteAMostrar;



 

  // Renderizaciones condicionales para estados de carga y error
  if (cargandoCarrito) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colores.rojo} />
        <Text style={styles.loadingText}>Cargando información del carrito...</Text>
      </View>
    );
  }

  if (errorCarrito) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <MaterialCommunityIcons name="alert-circle" size={50} color={colores.error} />
        <Text style={styles.errorText}>{errorCarrito}</Text>
        <TouchableOpacity
          style={styles.reloadButton}
          onPress={cargarProductosCarrito}
        >
          <Text style={styles.reloadButtonText}>Reintentar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  {/*if (productos.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <MaterialCommunityIcons name="cart-outline" size={50} color={colores.grisClaro} />
        <Text style={styles.emptyText}>Tu carrito está vacío</Text>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.navigate('Home')}
        >
          <Text style={styles.backButtonText}>Ir a comprar</Text>
        </TouchableOpacity>
      </View>
    );
  }*/}

  // Estilos dinámicos responsivos
  const dynamicStyles = {
    contenidoContainer: {
      ...styles.contenidoContainer,
      // En móvil: 
      // - Si showFormaPago es false (mostrando ResumenCompra), usar column-reverse (ResumenPedido arriba)
      // - Si showFormaPago es true (mostrando desplegables), usar column (ResumenPedido abajo)
      flexDirection: isMobile ? (showFormaPago ? 'column' : 'column-reverse') : 'row',
      width: isMobile ? '100%' : '90%',
    },
    seccionIzquierda: {
      ...styles.seccionIzquierda,
      width: isMobile ? '100%' : '65%',
      marginBottom: isMobile ? 15 : 0,
    },
    seccionDerecha: {
      ...styles.seccionDerecha,
      width: isMobile ? '100%' : '32%',
    },
    metodoEntregaBtn: {
      ...styles.metodoEntregaBtn,
      flex: 1,
    },
    metodoEntregaText: {
      ...styles.metodoEntregaText,
      fontSize: isMobile ? 16 : 20,
    },
    headerTitle: {
      ...styles.headerTitle,
      fontSize: isMobile ? 14 : 16,
    },
    botonesNavegacion: {
      ...styles.botonesNavegacion,
      // En móvil, mantener en la misma fila
      flexDirection: 'row',
      gap: isMobile ? 8 : 0,
    },
    botonSiguiente: {
      ...styles.botonSiguiente,
      // Botón más chico en móvil
      width: isMobile ? 'auto' : 'auto',
      paddingVertical: isMobile ? 10 : 12,
      paddingHorizontal: isMobile ? 16 : 25,
    },
    botonAnterior: {
      ...styles.botonAnterior,
      // Botón más chico en móvil
      width: isMobile ? 'auto' : 'auto',
      paddingVertical: isMobile ? 10 : 12,
      paddingHorizontal: isMobile ? 16 : 25,
    },
  };

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
    >
      {/* Componente de pasos del checkout con botón de volver */}
      <View style={styles.checkoutStepsWrapper}>
        <View style={styles.checkoutStepsContainer}>
          <CheckoutSteps 
            currentStep={!showFormaPago ? "resumen" : activeSection}
            onStepClick={handleStepClick}
          />
        </View>
        {showFormaPago && !isMobile && (
          <TouchableOpacity 
            style={styles.botonVolverSteps}
            onPress={volverAlResumen}
          >
            <MaterialCommunityIcons name="chevron-left" size={20} color={colores.rojo} />
            <Text style={styles.botonVolverStepsText}>Volver</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={dynamicStyles.contenidoContainer}>
        {/* Sección izquierda que cambia según el paso del checkout */}
        <View style={dynamicStyles.seccionIzquierda}>
          {/* Resumen de compra (visible sólo cuando showFormaPago es false) */}
          {!showFormaPago && (
            <ResumenCompra />
          )}

          {/* Método de entrega con cabecera desplegable */}
          {showFormaPago && (
            <View ref={formaPagoRef} style={styles.seccionDesplegable}>
              <TouchableOpacity
                style={[styles.headerDesplegable, activeSection === 'formaPago' && styles.headerActivo]}
                onPress={() => setActiveSection('formaPago')}
              >
                <Text style={dynamicStyles.headerTitle}>Método de entrega</Text>
                <MaterialCommunityIcons
                  name={activeSection === 'formaPago' ? "chevron-up" : "chevron-down"}
                  size={24}
                  color={colores.texto}
                />
              </TouchableOpacity>

              {activeSection === 'formaPago' && (
                <View style={styles.contenidoDesplegable}>
                  {/* Opciones de método de entrega */}
                  <View style={styles.metodoEntregaContainer}>                    
                    {/* Mensaje de días de envío si aplica */}
                    {tieneDiasEnvio && (
                      <View style={styles.mensajeDiasEnvio}>
                        <MaterialCommunityIcons name="information" size={20} color={colores.rojo} />
                        <Text style={styles.textoDiasEnvio}>
                          El tiempo estimado de entrega es de {maxDiasEnvio} {maxDiasEnvio === 1 ? 'día' : 'días'} hábiles.
                        </Text>
                      </View>
                    )}

                    <View style={[
                      styles.botonesMetodoContainer,
                      tipoEntrega === null && { minHeight: 140}
                    ]}>
                      {tipoEntrega === null && (
                        <View style={styles.mensajeSeleccionContainer}>
                          <Text style={styles.mensajeSeleccionTexto}>Elegí una opción de entrega</Text>
                        </View>
                      )}
                      <TouchableOpacity
                        style={[dynamicStyles.metodoEntregaBtn,
                        { borderRightWidth: 0 },
                        tipoEntrega === 2 ? styles.metodoSeleccionado : styles.metodoNoSeleccionado,
                        ]}
                        onPress={() => setTipoEntrega(2)}
                      >
                        <Text style={[dynamicStyles.metodoEntregaText, tipoEntrega === 2 ? styles.textoMetodoSeleccionado : null]}>Envio</Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={[dynamicStyles.metodoEntregaBtn,
                          tipoEntrega === 1 ? styles.metodoSeleccionado : styles.metodoNoSeleccionado,
                          tieneDiasEnvio && styles.metodoDeshabilitado,
                        ]}
                        onPress={() => !tieneDiasEnvio && setTipoEntrega(1)}
                        disabled={tieneDiasEnvio}
                      >
                        <Text style={[dynamicStyles.metodoEntregaText, tipoEntrega === 1 ? styles.textoMetodoSeleccionado : null, tieneDiasEnvio && styles.textoMetodoDeshabilitado]}>Retiro</Text>
                      </TouchableOpacity>
                    </View>
                  </View>

                  {/* Mostrar el componente correspondiente según la selección */}
                  {tipoEntrega !== null && (
                    <>
                      <View style={styles.metodoEntregaInfo}>
                        {tipoEntrega === 2 ? (
                          <Envio ref={envioRef} />
                        ) : (
                          <Retiro/>
                        )}
                      </View>
                      <View style={dynamicStyles.botonesNavegacion}>
                        <TouchableOpacity 
                          style={dynamicStyles.botonAnterior}
                          onPress={volverAlResumen}
                        >
                          <Text style={styles.botonAnteriorTexto}>Anterior</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            dynamicStyles.botonSiguiente,
                            (tipoEntrega === null || (tipoEntrega === 2 && !codDic)) && styles.botonDeshabilitado
                          ]}
                          onPress={() => (tipoEntrega !== null && (tipoEntrega === 1 || codDic)) && avanzarSeccion('formaPago', 'horarioRetiro')}
                          disabled={tipoEntrega === null || (tipoEntrega === 2 && !codDic)}
                        >
                          <Text style={[
                            styles.botonSiguienteTexto,
                            (tipoEntrega === null || (tipoEntrega === 2 && !codDic)) && styles.textoBotonDeshabilitado
                          ]}>Siguiente</Text>
                        </TouchableOpacity>
                      </View>
                    </>
                  )}
                </View>
              )}
            </View>
          )}

          {/* Horario de retiro con cabecera desplegable */}
          {showFormaPago && (
            <View ref={horarioRetiroRef} style={[styles.seccionDesplegable, { marginBottom: 10 }]}> 
              <TouchableOpacity
                style={[
                  styles.headerDesplegable,
                  activeSection === 'horarioRetiro' && styles.headerActivo,
                  (!seccionesHabilitadas.horarioRetiro || tipoEntrega === null) && styles.headerDeshabilitado
                ]}
                onPress={() => seccionesHabilitadas.horarioRetiro && tipoEntrega !== null && setActiveSection('horarioRetiro')}
                disabled={!seccionesHabilitadas.horarioRetiro || tipoEntrega === null}
              >
                <Text style={[
                  styles.headerTitle,
                  (!seccionesHabilitadas.horarioRetiro || tipoEntrega === null) && styles.headerTitleDeshabilitado
                ]}>Horario de retiro</Text>
                <MaterialCommunityIcons
                  name={activeSection === 'horarioRetiro' ? "chevron-up" : "chevron-down"}
                  size={24}
                  color={(!seccionesHabilitadas.horarioRetiro || tipoEntrega === null) ? colores.grisClaro : colores.texto}
                />
              </TouchableOpacity>

              {activeSection === 'horarioRetiro' && (
                <View style={styles.contenidoDesplegable}>
                  {tieneDiasEnvio ? (
                    // Mostrar mensaje de días de envío en lugar del selector de horario
                    <View style={styles.mensajeDiasEnvioHorario}>
                      <MaterialCommunityIcons name="clock-outline" size={24} color={colores.rojo} />
                      <Text style={styles.textoDiasEnvioHorario}>
                        El tiempo de entrega estimado es de {maxDiasEnvio} {maxDiasEnvio === 1 ? 'día' : 'días'} hábiles.
                      </Text>
                    </View>
                  ) : (
                    // Mostrar selector de horario normal
                    <HorarioRetiro/>
                  )}

                  <View style={dynamicStyles.botonesNavegacion}>
                    <TouchableOpacity
                      style={dynamicStyles.botonAnterior}
                      onPress={() => volverSeccion('horarioRetiro', 'formaPago')}
                    >
                      <Text style={styles.botonAnteriorTexto}>Anterior</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        dynamicStyles.botonSiguiente,
                        (!fechaRetiroSeleccionada || !codigoHorarioSeleccionado) && styles.botonDeshabilitado
                      ]}
                      onPress={() => (fechaRetiroSeleccionada && codigoHorarioSeleccionado) && avanzarSeccion('horarioRetiro', 'facturacion')}
                      disabled={!fechaRetiroSeleccionada || !codigoHorarioSeleccionado}
                    >
                      <Text style={[
                        styles.botonSiguienteTexto,
                        (!fechaRetiroSeleccionada || !codigoHorarioSeleccionado) && styles.textoBotonDeshabilitado
                      ]}>Siguiente</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Datos de facturación con cabecera desplegable */}
          {showFormaPago && (
            <View ref={facturacionRef} style={[styles.seccionDesplegable, { marginBottom: 20 }]}> 
              <TouchableOpacity
                style={[
                  styles.headerDesplegable,
                  activeSection === 'facturacion' && styles.headerActivo,
                  (!seccionesHabilitadas.facturacion || tipoEntrega === null) && styles.headerDeshabilitado
                ]}
                onPress={() => seccionesHabilitadas.facturacion && tipoEntrega !== null && setActiveSection('facturacion')}
                disabled={!seccionesHabilitadas.facturacion || tipoEntrega === null}
              >
                <Text style={[
                  styles.headerTitle,
                  (!seccionesHabilitadas.facturacion || tipoEntrega === null) && styles.headerTitleDeshabilitado
                ]}>Datos de facturación</Text>
                <MaterialCommunityIcons
                  name={activeSection === 'facturacion' ? "chevron-up" : "chevron-down"}
                  size={24}
                  color={(!seccionesHabilitadas.facturacion || tipoEntrega === null) ? colores.grisClaro : colores.texto}
                />
              </TouchableOpacity>

              {activeSection === 'facturacion' && (
                <View style={styles.contenidoDesplegable}>
                  <DatosFacturacion/>

                  <View style={dynamicStyles.botonesNavegacion}>
                    <TouchableOpacity
                      style={dynamicStyles.botonAnterior}
                      onPress={() => volverSeccion('facturacion', 'horarioRetiro')}
                    >
                      <Text style={styles.botonAnteriorTexto}>Anterior</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={dynamicStyles.botonSiguiente}
                      onPress={() => avanzarSeccion('facturacion', 'metodoPago')}
                    >
                      <Text style={styles.botonSiguienteTexto}>Siguiente</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Forma de pago con cabecera desplegable */}
          {showFormaPago && (
            <View ref={metodoPagoRef} style={styles.seccionDesplegable}>
              <TouchableOpacity
                style={[
                  styles.headerDesplegable,
                  activeSection === 'metodoPago' && styles.headerActivo,
                  (!seccionesHabilitadas.metodoPago || tipoEntrega === null) && styles.headerDeshabilitado
                ]}
                onPress={() => seccionesHabilitadas.metodoPago && tipoEntrega !== null && setActiveSection('metodoPago')}
                disabled={!seccionesHabilitadas.metodoPago || tipoEntrega === null}
              >
                <Text style={[
                  styles.headerTitle,
                  (!seccionesHabilitadas.metodoPago || tipoEntrega === null) && styles.headerTitleDeshabilitado
                ]}>Forma de pago</Text>
                <MaterialCommunityIcons
                  name={activeSection === 'metodoPago' ? "chevron-up" : "chevron-down"}
                  size={24}
                  color={(!seccionesHabilitadas.metodoPago || tipoEntrega === null) ? colores.grisClaro : colores.texto}
                />
              </TouchableOpacity>

              {activeSection === 'metodoPago' && (
                <View style={styles.contenidoDesplegable}>
                  {/* Integración del componente FormaPago */}
                  <FormasDePago
                    route={{
                      params: {
                        productos: productos,
                        subtotal: subtotal,
                        descuentoTotal: descuentoTotal,
                        total: (effectiveTotal && effectiveTotal > 0) ? effectiveTotal : total,
                        cupon: cupon || null
                      }
                    }}
                    customBackAction={() => volverSeccion('metodoPago', 'facturacion')}
                  />

                  <View style={dynamicStyles.botonesNavegacion}>
                    <TouchableOpacity
                      style={dynamicStyles.botonAnterior}
                      onPress={() => volverSeccion('metodoPago', 'facturacion')}
                    >
                      <Text style={styles.botonAnteriorTexto}>Anterior</Text>
                    </TouchableOpacity>

                    {(Platform.OS !== 'web' || isMobile) && (
                      <TouchableOpacity
                        style={dynamicStyles.botonSiguiente}
                        onPress={() => {
                          // Hacer scroll al resumen pedido solo en móvil
                          if (isMobile && Platform.OS === 'web') {
                            setTimeout(() => {
                              if (resumenPedidoRef && resumenPedidoRef.current) {
                                resumenPedidoRef.current.scrollIntoView({ 
                                  behavior: 'smooth', 
                                  block: 'start',
                                  inline: 'nearest'
                                });
                              }
                            }, 100);
                          }
                        }}
                      >
                        <Text style={styles.botonSiguienteTexto}>Siguiente</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Sección derecha fija y siempre visible */}
        <View ref={resumenPedidoRef} style={dynamicStyles.seccionDerecha}>
          <ResumenPedido
            // Datos de la sucursal
            sucursalId={sucursalId}
            sucursalCiudad={sucursalCiudad}
            sucursalDireccion={sucursalDireccion}
            sucursalTelefono={sucursalTelefono}
            
            // Datos de cupones y descuentos
            cupon={cupon}
            setCupon={setCupon}
            cargando={cargando}
            setDescuentoAplicado={setDescuentoAplicado}
            descuentoAplicado={descuentoAplicado}
            onEffectiveTotalChange={(t) => setEffectiveTotal(Number(t) || 0)}
            
            // Controles de estado
            showFormaPago={showFormaPago}
            setShowFormaPago={setShowFormaPago}
            modalFleteVisible={modalFleteVisible}
            setModalFleteVisible={setModalFleteVisible}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({

  // Estilos para métodos de entrega
  metodoEntregaContainer: {
    width: '100%',
  },
  botonesMetodoContainer: {
    flexDirection: 'row',
    marginVertical: 10,
    width: '100%',
  },
  metodoEntregaBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    height: 40,
    borderWidth: 1,
  },
  metodoSeleccionado: {
    backgroundColor: colores.rojo,
    borderColor: colores.rojo,
  },
  metodoNoSeleccionado: {
    backgroundColor: '#E5E5E0',
    borderColor: '#E5E5E0',
  },
  textoMetodoSeleccionado: {
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  metodoEntregaText: {
    fontWeight: 'bold',
    fontSize: 20
    ,
  },
  mensajeSeleccionContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 0,
  },
  mensajeSeleccionTexto: {
    fontSize: 16,
    color: colores.textoSecundario,
    fontFamily: fuentes.medium,
    textAlign: 'center',
  },
  metodoEntregaInfo: {
    //marginTop: 15,
    width: '100%',
  },
  sucursalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    backgroundColor: colores.blanco,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  sucursalNombre: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  sucursalDireccion: {
    fontSize: 14,
    color: '#555',
    marginBottom: 3,
  },
  sucursalTelefono: {
    fontSize: 14,
    color: '#777',
  },

  seccionDesplegable: {
    backgroundColor: colores.blanco,
    borderRadius: 5,
    overflow: 'hidden',
    width: '100%',
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
    position: 'relative',
    zIndex: 0,
  },
  headerDesplegable: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: 15,
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.textoPrincipal,

  },
  contenidoDesplegable: {
    padding: 15,
  },
  botonesNavegacion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: 5,
  },
  botonSiguiente: {
    backgroundColor: colores.rojo,
    paddingVertical: 12,
    paddingHorizontal: 25,
    borderRadius: 5,
  },
  botonAnterior: {
    backgroundColor: '#E5E5E0',
    paddingVertical: 12,
    paddingHorizontal: 25,
    borderRadius: 5,
  },
  botonAnteriorTexto: {
    color: '#333',
    fontWeight: 'bold',
    fontSize: 16,
  },
  headerActivo: {
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
  },
  botonSiguienteTexto: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 16,
  },
  container: {
    flex: 1,
    backgroundColor: colores.fondo,
    position: 'relative',
    zIndex: 0,
  },
  contentContainer: {
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    zIndex: 0,
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: colores.textoPrincipal,
  },
  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: colores.error,
    textAlign: 'center',
  },
  emptyText: {
    marginTop: 10,
    fontSize: 16,
    color: colores.grisOscuro,
    textAlign: 'center',
  },
  checkoutStepsWrapper: {
    position: 'relative',
    marginBottom: 20,
    width: '100%',
  },
  checkoutStepsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15,
    width: '90%',
    alignSelf: 'center',
  },
  botonVolverSteps: {
    position: 'absolute',
    left: '5%',
    top: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: colores.rojo,
  },
  botonVolverStepsMobile: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    left: 10,
    top: 8,
  },
  botonVolverStepsText: {
    color: colores.rojo,
    fontWeight: 'bold',
    fontSize: 14,
    marginLeft: 5,
  },
  headerDesplegableContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
  },
  botonVolverMetodo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 5,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: colores.rojo,
  },
  botonVolverMetodoText: {
    color: colores.rojo,
    fontWeight: 'bold',
    fontSize: 12,
  },
  reloadButton: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: colores.rojo,
    borderRadius: 5,
  },
  reloadButtonText: {
    color: colores.blanco,
    fontSize: 14,
    fontWeight: 'bold',
  },
  backButton: {
    marginTop: 20,
    paddingVertical: 10,
  },

  backButtonText: {
    color: colores.blanco,
    fontSize: 14,
    fontWeight: 'bold',
  },

  contenidoContainer: {
    flexDirection: 'row',
    width: '90%',
    paddingBottom: 15,
    justifyContent: 'space-between',
    alignSelf: 'center',
    position: 'relative',
    zIndex: 0,
  },
  seccionIzquierda: {
    width: Platform.OS === 'web' ? '65%' : '100%',
    marginBottom: Platform.OS === 'web' ? 0 : 20,
    position: 'relative',
    zIndex: 0,
  },
  seccionDerecha: {
    width: Platform.OS === 'web' ? '35%' : '100%',
    alignSelf: 'flex-start',
    position: 'relative',
    zIndex: 0,
  },

  seccionSuperiorFija: {
    width: '100%',
    backgroundColor: colores.blanco,
    padding: 15,
    position: Platform.OS === 'web' ? 'sticky' : 'relative',
    top: Platform.OS === 'web' ? 0 : undefined,
    zIndex: 1,
  },
  tituloSeccion: {
    fontWeight: 'bold',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 12,
    color: colores.rojo,
  },
  seccionTitulo: {
    fontFamily: fuentes.bold,
    fontSize: 16,
    textAlign: 'center',
  },
  sucursalTitulo: {
    fontFamily: fuentes.bold,
    fontSize: 14,
    marginBottom: 5,
    color: colores.texto,
    textAlign: 'center',
  },
  sucursalDireccion: {
    fontFamily: fuentes.regular,
    fontSize: 13,
    color: colores.texto,
    marginBottom: 3,
    textAlign: 'center',
  },
  sucursalTelefono: {
    fontFamily: fuentes.regular,
    fontSize: 13,
    color: colores.texto,
    textAlign: 'center',
  },

  // Estilos para los totales
  totalesContainer: {
    width: '100%',
    marginBottom: 20,
    backgroundColor: '#f9f9f9',
    padding: 15,
    borderRadius: 8,
  },
  lineaTotales: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  textoTotales: {
    fontFamily: fuentes.regular,
    fontSize: 14,
    color: colores.texto,
  },
  valorTotales: {
    fontFamily: fuentes.bold,
    fontSize: 14,
    color: colores.texto,
  },
  valorDescuento: {
    fontFamily: fuentes.bold,
    fontSize: 14,
    color: '#2a9d38',
  },
  totalFinal: {
    borderTopWidth: 1,
    borderTopColor: colores.grisClaro,
    paddingTop: 10,
    marginTop: 10,
  },
  textoTotal: {
    fontFamily: fuentes.bold,
    fontSize: 16,
    color: colores.texto,
  },
  valorTotal: {
    fontFamily: fuentes.bold,
    fontSize: 18,
    color: colores.rojo,
  },
  separadorFinanciacion: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: colores.grisClaro,
    marginBottom: 10,
  },
  tituloFinanciacion: {
    fontFamily: fuentes.bold,
    fontSize: 14,
    color: colores.textoPrincipal,
    textAlign: 'center',
    marginBottom: 5,
  },
  // Estilos para los botones
  botonContinuar: {
    backgroundColor: colores.rojo,
    paddingVertical: 15,
    borderRadius: 5,
    marginTop: 20,
    alignItems: 'center',
  },
  botonDeshabilitado: {
    backgroundColor: '#cccccc',
    opacity: 0.7,
  },
  botonContinuarTexto: {
    color: colores.blanco,
    fontFamily: fuentes.bold,
    fontSize: 16,
    textTransform: 'uppercase',
  },

  botonSeguirComprandoTexto: {
    color: colores.rojo,
    textAlign: 'center',
    fontSize: 15,
    marginBottom: 15,
    marginTop: 15,
  },
  mensajeDiasEnvio: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF5F5',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: colores.rojo,
    gap: 10,
  },
  textoDiasEnvio: {
    flex: 1,
    fontSize: 14,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal,
    lineHeight: 20,
  },
  metodoDeshabilitado: {
    opacity: 0.5,
    backgroundColor: '#f5f5f5',
  },
  textoMetodoDeshabilitado: {
    color: '#999',
  },
  mensajeDiasEnvioHorario: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF5F5',
    padding: 20,
    borderRadius: 8,
    marginVertical: 16,
    borderLeftWidth: 3,
    borderLeftColor: colores.rojo,
    gap: 12,
  },
  textoDiasEnvioHorario: {
    flex: 1,
    fontSize: 15,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    lineHeight: 22,
  },
});

export default GestionFinalizar;
